/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-01 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, Button, Link, TextStyle } from '@shopify/polaris';
import { MobileVerticalDotsMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';

import TableCell from './table-cell';
import SelectProductModal from 'src/components/containers/search-page-display/select-product-modal';
import { NUMBER_PRODUCT_SUGGESTIONS } from 'src/constants/search-page-display';
import { getShop } from 'src/helpers';
import noProductImage from 'src/assets/images/product/no-product-image.png';
import { optimizeImage } from 'src/helpers';
import Delete from 'src/assets/images/common/delete_minor.svg';

type Props = {
  field: Object,
  productSuggestionsList: Array<Object>
};

type ComposedProps = Props & WithI18nProps;

type State = {
  active: boolean
};
class ProductTable extends Component<ComposedProps, State> {
  state = {
    active: false
  };

  onDragEnd = (result: DropResult) => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
  };

  render() {
    const { fields, productSuggestionsList, fieldName, i18n } = this.props;
    const { active } = this.state;

    return (
      <div>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <div className="Polaris-DataTable BC-DataTable__Product">
            {_.isEmpty(fields) ? (
              <div className="empty-product">
                <TextStyle variation="subdued">
                  {i18n.translate('AdminHTML.SearchPageDisplay.Common.productTable_NoProduct')}
                </TextStyle>
              </div>
            ) : (
              <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
                <table className="Polaris-DataTable__Table">
                  <Droppable droppableId="searchTermTable">
                    {provided => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {fields.map((item, key) => {
                          const product = productSuggestionsList[key];
                          const shop = getShop();
                          const url = `https://${shop}/admin/products/${product.id}`;
                          const { images } = product;
                          let image = _.isEmpty(images)
                            ? noProductImage
                            : optimizeImage(images[Object.keys(images)[0]], '40x40');
                          return (
                            // Enable drag for per option row
                            <Draggable draggableId={`${item}-${key}`} key={item} index={key}>
                              {(provided, snapshot) => (
                                <tr
                                  className="Polaris-DataTable__TableRow"
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}>
                                  {/* Drag icon */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                    <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                  </TableCell>

                                  {/* image */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <div
                                      className={`product-image ${
                                        _.isEmpty(images) ? 'no-image' : ''
                                      }`}>
                                      <img src={image} alt={product.title} />
                                    </div>
                                  </TableCell>

                                  {/* title */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <div>
                                      <Link removeUnderline external url={url}>
                                        {product.title}
                                      </Link>
                                    </div>
                                  </TableCell>
                                  {/* Action */}
                                  <TableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                    <Button
                                      size="slim"
                                      icon={<img src={Delete} alt="delete" />}
                                      onClick={() => {
                                        this.handleRemove(key);
                                      }}
                                    />
                                  </TableCell>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </table>
              </div>
            )}
            <div className="addProducts">
              <Button
                plain
                onClick={this.toggleModal}
                disabled={productSuggestionsList.length === NUMBER_PRODUCT_SUGGESTIONS}>
                {i18n.translate('AdminHTML.SearchPageDisplay.Common.productTable_ButtonChildren')}
              </Button>
            </div>
          </div>
        </DragDropContext>
        <SelectProductModal
          active={active}
          toggleModal={this.toggleModal}
          productSuggestionsList={productSuggestionsList}
          fieldName={fieldName}
        />
      </div>
    );
  }

  toggleModal = () => {
    const { active } = this.state;

    this.setState({ active: !active });
  };

  handleRemove = key => {
    const { fields } = this.props;

    fields.remove(key);
  };
}
export default withI18n()(ProductTable);
